/**
 * Highlights the words a search matched in result lists and the opened message. Matching is
 * case- and diacritics-insensitive (`über` finds `Uber`); it works on plain substrings with
 * index maps, never on a pattern built from the query, so no query text can act as markup or as
 * a regular expression.
 */
import { highlightTerms, parseSearchQuery, type HighlightTerms } from '@platform/search-query';
import type { MailHighlighter } from '@platform/ui';
import { Fragment, type ReactNode } from 'react';
import type { SearchRequest } from './api';

interface Folded {
  text: string;
  /** Original index range [start, end) of every folded character. */
  starts: number[];
  ends: number[];
}

const MARKS = /\p{M}/gu;

/** Lower-cases and strips diacritics (and ß -> ss), remembering where each folded character came from. */
function fold(text: string): Folded {
  const folded: Folded = { text: '', starts: [], ends: [] };
  let index = 0;
  for (const char of text) {
    const next = index + char.length;
    const part = char.normalize('NFD').replace(MARKS, '').toLocaleLowerCase().replace(/ß/g, 'ss');
    for (const piece of part) {
      folded.text += piece;
      for (let unit = 0; unit < piece.length; unit++) {
        folded.starts.push(index);
        folded.ends.push(next);
      }
    }
    index = next;
  }
  return folded;
}

export interface Segment {
  text: string;
  match: boolean;
}

/** Splits `text` into plain and matched parts (overlapping matches are merged). */
export function segmentMatches(text: string, terms: readonly string[]): Segment[] {
  const needles = terms.map((term) => fold(term.trim()).text).filter(Boolean);
  if (!text || needles.length === 0) return [{ text, match: false }];
  const haystack = fold(text);
  const ranges: Array<[number, number]> = [];
  for (const needle of needles) {
    for (let at = haystack.text.indexOf(needle); at >= 0; at = haystack.text.indexOf(needle, at + 1)) {
      ranges.push([haystack.starts[at]!, haystack.ends[at + needle.length - 1]!]);
    }
  }
  if (ranges.length === 0) return [{ text, match: false }];
  ranges.sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const merged: Array<[number, number]> = [];
  for (const [start, end] of ranges) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  const segments: Segment[] = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    if (start > cursor) segments.push({ text: text.slice(cursor, start), match: false });
    segments.push({ text: text.slice(start, end), match: true });
    cursor = end;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), match: false });
  return segments;
}

/** React nodes for a text with `<mark>` around the matches (React escapes the text itself). */
export function highlightNodes(text: string, terms: readonly string[]): ReactNode {
  const segments = segmentMatches(text, terms);
  if (segments.length === 1 && !segments[0]!.match) return text;
  return segments.map((segment, index) =>
    segment.match ? (
      <mark key={index} className="rounded-sm bg-yellow-300 px-0.5 text-stone-900">
        {segment.text}
      </mark>
    ) : (
      <Fragment key={index}>{segment.text}</Fragment>
    ),
  );
}

/** Elements whose text is not message content. */
const SKIP = new Set(['STYLE', 'SCRIPT', 'TEXTAREA', 'MARK']);

/**
 * Wraps matches in the text nodes of already sanitized HTML in `<mark>`. Only text nodes are
 * split; the markup itself is never rebuilt from the query.
 */
export function highlightHtml(html: string, terms: readonly string[]): string {
  if (!html || terms.every((term) => !term.trim())) return html;
  const template = document.createElement('template');
  template.innerHTML = html;
  const texts: Text[] = [];
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => (node.parentElement && SKIP.has(node.parentElement.tagName) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  for (let node = walker.nextNode(); node; node = walker.nextNode()) texts.push(node as Text);
  let changed = false;
  for (const node of texts) {
    const segments = segmentMatches(node.data, terms);
    if (segments.length === 1 && !segments[0]!.match) continue;
    changed = true;
    const fragment = document.createDocumentFragment();
    for (const segment of segments) {
      if (!segment.match) fragment.append(document.createTextNode(segment.text));
      else {
        const mark = document.createElement('mark');
        mark.textContent = segment.text;
        fragment.append(mark);
      }
    }
    node.replaceWith(fragment);
  }
  return changed ? template.innerHTML : html;
}

/** Words to highlight for a search: the query's free text, subject:/body: values and the filter's subject/body fields. */
export function searchHighlightTerms(query: string, filter: SearchRequest['filter'], locale: 'en' | 'de'): HighlightTerms {
  const parsed = parseSearchQuery(query, locale);
  const terms = parsed.ok ? highlightTerms(parsed.ast) : { subject: [], body: [] };
  if (filter?.subject) terms.subject.push(filter.subject);
  if (filter?.body) terms.body.push(filter.body);
  return terms;
}

/** The highlighter for the list and reading pane, or undefined when there is nothing to mark. */
export function createHighlighter(terms: HighlightTerms): MailHighlighter | undefined {
  if (terms.subject.length === 0 && terms.body.length === 0) return undefined;
  return {
    text: (text, place) => highlightNodes(text, terms[place]),
    html: (html) => highlightHtml(html, terms.body),
  };
}
